import { Component, OnInit, Input, ViewChild, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Course } from '@app/core/model/library.model';
import { LazyLoad } from '@app/core/model/pagination.model';
import { ByocService } from '@app/byoc/popup/service/byoc.service';
import { ByocMissionService } from '../../../service/mission.service';
import { PerfectScrollbarDirective } from '@app/shared/perfect-scrollbar/perfect-scrollbar.directive';

@Component({
    selector: 'app-byoc-mission-step-course-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    standalone: false
})
export class ByocMissionStepCourseListComponent implements OnInit, OnDestroy {
    @ViewChild('ps', { read: PerfectScrollbarDirective, static: true })
    psRef?: PerfectScrollbarDirective;

    @Input()
    searchFormControl!: UntypedFormControl;

    @Input()
    originControl!: UntypedFormControl;

    @Input()
    onHold = false;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly byocService = inject(ByocService);
    private readonly byocMissionService = inject(ByocMissionService);

    /**
     * Search Delay
     */
    delay: any;

    coursesList: Course[] = [];

    /**
     * courses added in popup
     */
    newlyCreatedCourses: Course[] = [];

    /**
     * newly created courses that are not yet openable
     */
    inactiveCourses: string[] = [];

    /**
     * Is Searching flag
     */
    searching = false;

    /**
     * Subscriptions
     */
    private subscriptions: Subscription = new Subscription();

    /**
     * Search Subscription
     */
    private subscriptionSearch: Subscription = new Subscription();

    /**
     * lazyLoad
     */
    private lazyLoad: LazyLoad = {
        offset: {
            prefixPosition: 0,
            suffixPosition: 0,
        },
        totalItems: 0,
        limit: 50,
    };

    ngOnInit() {
        this.search();
        this.subscriptions.add(
            this.searchFormControl.valueChanges.subscribe(() => {
                this.newlyCreatedCourses.splice(0);
                this.search();
            }),
        );
        this.subscriptions.add(
            this.originControl.valueChanges.subscribe(() => {
                this.inactiveCourses.length = 0;
                this.newlyCreatedCourses.length = 0;
                this.search();
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        if (this.subscriptionSearch) {
            this.subscriptionSearch.unsubscribe();
        }
        if (this.delay) {
            clearTimeout(this.delay);
        }
    }

    get coursesFormGroup() {
        return this.byocMissionService.coursesFormGroup;
    }

    get scrollbarYEnabled(): boolean {
        return this.psRef?.ps?.scrollbarYActive ?? false;
    }

    /**
     * Y Reached end event
     */
    psYReachEnd() {
        if (this.onHold) {
            return;
        }
        this.append();
    }

    /**
     * No result found
     */
    get noResults(): boolean {
        return !this.searching && this.lazyLoad.totalItems === 0;
    }

    /**
     * Search
     */
    search(): void {
        this.psRef?.scrollToTop();

        // unsubscribe from active
        if (this.subscriptionSearch) {
            this.subscriptionSearch.unsubscribe();
        }

        // clear timtout from active
        if (this.delay) {
            clearTimeout(this.delay);
        }

        // reset lazyLoad
        this.resetLazyLoad();

        this.searching = true;
        this.changeDetectorRef.detectChanges();

        this.delay = setTimeout(() => {
            this.subscriptionSearch = this.byocService
                .searchCourse(this.searchFormControl.value, this.originControl.value, this.lazyLoad.limit, this.lazyLoad.offset.suffixPosition)
                .subscribe((data) => {
                    const newCoursesList = data.courses.filter((course) => !this.inactiveCourses.includes(course.id));

                    this.coursesList = [];
                    // this.byocMissionService.removeAllCourseControls();
                    this.setCoursesControls(newCoursesList);
                    this.setCoursesControls(this.newlyCreatedCourses);
                    this.coursesList = newCoursesList;

                    this.lazyLoad.totalItems = data.pagination.totalItems;
                    this.lazyLoad.offset.suffixPosition += data.courses.length;
                    this.searching = false;
                    this.changeDetectorRef.detectChanges();
                    this.psRef?.ps.update();
                    // need to refresh
                    setTimeout(() => {
                        this.changeDetectorRef.detectChanges();
                    }, 0);
                });
        }, 250);
    }

    isCourseInactive(course: Course) {
        return course.stats && course.stats.empty;
    }

    private setCoursesControls(courses: Course[]) {
        courses.forEach((course) => {
            this.byocMissionService.addCourseControl(course.id);
        });

        this.changeDetectorRef.detectChanges();
    }

    /**
     * Append Search
     */
    private append(): void {
        // don't run when already saerching
        if (this.searching) {
            return;
        }

        // don't run when all loaded
        if (this.coursesList.length + this.newlyCreatedCourses.length >= this.lazyLoad.totalItems) {
            return;
        }
        this.searching = true;
        this.changeDetectorRef.detectChanges();

        this.subscriptionSearch = this.byocService
            .searchCourse(this.searchFormControl.value, this.originControl.value, this.lazyLoad.limit, this.lazyLoad.offset.suffixPosition)
            .subscribe((data) => {
                const newCoursesList = this.coursesList.concat(data.courses).filter((course) => !this.inactiveCourses.includes(course.id));

                this.lazyLoad.totalItems = data.pagination.totalItems;
                this.lazyLoad.offset.suffixPosition += data.courses.length;
                this.setCoursesControls(newCoursesList);
                this.coursesList = newCoursesList;

                this.searching = false;
                this.changeDetectorRef.detectChanges();
                this.psRef?.ps?.update();
                // need to refresh
                setTimeout(() => {
                    this.changeDetectorRef.detectChanges();
                }, 0);
            });
    }

    private resetLazyLoad(): void {
        this.lazyLoad = {
            offset: {
                prefixPosition: 0,
                suffixPosition: 0,
            },
            totalItems: 0,
            limit: 50,
        };
    }
}
